// Score Source Utilities - Determines data source and provides prerequisite/scoring logic
// Used across PursuitRiskWidget, GoNoGoDashboard, OpportunityIntake, and PursuitPipeline

/**
 * Score Source Types (Redesigned)
 * PREREQUISITES_INCOMPLETE: Missing required metadata or documents
 * ANALYSIS_REQUIRED: Prerequisites met, but AI Analysis hasn't run yet
 * AI_ANALYZED: Score derived from AI analysis of RFP vs capabilities
 */
export const SCORE_SOURCE = {
  PREREQUISITES_INCOMPLETE: 'PREREQUISITES_INCOMPLETE',
  ANALYSIS_REQUIRED: 'ANALYSIS_REQUIRED',
  AI_ANALYZED: 'AI_ANALYZED',
  // Legacy aliases (kept for backward compat during transition)
  METADATA_ONLY: 'PREREQUISITES_INCOMPLETE',
  DOCUMENT_VERIFIED: 'AI_ANALYZED',
  NOT_ANALYZED: 'ANALYSIS_REQUIRED'
};

/**
 * Badge styling configuration for score sources
 */
export const SCORE_SOURCE_CONFIG = {
  [SCORE_SOURCE.PREREQUISITES_INCOMPLETE]: {
    label: 'PREREQUISITES INCOMPLETE',
    shortLabel: 'Incomplete',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    borderColor: 'border-red-200',
    description: 'Complete required fields and upload documents before scoring is available.'
  },
  [SCORE_SOURCE.ANALYSIS_REQUIRED]: {
    label: 'ANALYSIS REQUIRED',
    shortLabel: 'Ready',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    borderColor: 'border-amber-200',
    description: 'Prerequisites met. Run AI Analysis on the Ingestion Hub to calculate Fit Score.'
  },
  [SCORE_SOURCE.AI_ANALYZED]: {
    label: 'AI ANALYZED',
    shortLabel: 'Analyzed',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
    borderColor: 'border-emerald-200',
    description: 'Fit Score calculated from AI analysis of RFP requirements vs team capabilities.'
  }
};

/**
 * Checks prerequisite status for Fit Score calculation
 * Prerequisites are gates (not scores) — they must be satisfied before scoring is possible.
 * 
 * @param {Object} pursuit - The pursuit object
 * @returns {Object} { allMet: boolean, metadata: { complete, missing[] }, documents: { complete, missing[] } }
 */
export function getPrerequisiteStatus(pursuit) {
  if (!pursuit) {
    return {
      allMet: false,
      metadata: { complete: false, missing: ['Agency', 'NAICS Code', 'Description'] },
      documents: { complete: false, missing: ['RFP/Solicitation Document', 'Capability Document'] }
    };
  }

  // Metadata prerequisites
  const metadataMissing = [];
  if (!(pursuit.agency_id || pursuit.agency_name || pursuit.agency_code || pursuit.contracting_agency)) {
    metadataMissing.push('Agency');
  }
  if (!(pursuit.naics_code || pursuit.primary_naics)) {
    metadataMissing.push('NAICS Code');
  }
  if (!(pursuit.opportunity_name || pursuit.title || pursuit.description)) {
    metadataMissing.push('Opportunity Description');
  }

  // Document prerequisites
  const documentsMissing = [];
  const rfpCount = pursuit.rfp_document_count || 0;
  const capCount = pursuit.capability_document_count || 0;
  
  if (rfpCount === 0) {
    documentsMissing.push('RFP/Solicitation Document');
  }
  if (capCount === 0) {
    documentsMissing.push('Capability Document');
  }

  const metadataComplete = metadataMissing.length === 0;
  const documentsComplete = documentsMissing.length === 0;

  return {
    allMet: metadataComplete && documentsComplete,
    metadata: { complete: metadataComplete, missing: metadataMissing },
    documents: { complete: documentsComplete, missing: documentsMissing }
  };
}

/**
 * Determines the score source based on pursuit data
 * @param {Object} pursuit - The pursuit object
 * @returns {string} SCORE_SOURCE value
 */
export function getScoreSource(pursuit) {
  if (!pursuit) return SCORE_SOURCE.PREREQUISITES_INCOMPLETE;

  // Check if AI analysis has been completed
  const hasAIAnalysis = pursuit.ai_analysis_completed === true;

  if (hasAIAnalysis) {
    return SCORE_SOURCE.AI_ANALYZED;
  }

  // Check prerequisites
  const prereqs = getPrerequisiteStatus(pursuit);
  if (prereqs.allMet) {
    return SCORE_SOURCE.ANALYSIS_REQUIRED;
  }

  return SCORE_SOURCE.PREREQUISITES_INCOMPLETE;
}

/**
 * Gets the effective score to display
 * Score is ONLY available after AI Analysis has run.
 * Before that, returns null with the appropriate state.
 * 
 * @param {Object} pursuit - The pursuit object
 * @param {Object} organization - Optional organization for context
 * @returns {Object} { score: number|null, source: SCORE_SOURCE, prerequisites: Object }
 */
export function getEffectiveScore(pursuit, organization = null) {
  if (!pursuit) {
    return { score: null, source: SCORE_SOURCE.PREREQUISITES_INCOMPLETE, prerequisites: getPrerequisiteStatus(null) };
  }

  const source = getScoreSource(pursuit);
  const prerequisites = getPrerequisiteStatus(pursuit);

  // Only return a score when AI analysis has been completed
  if (source === SCORE_SOURCE.AI_ANALYZED) {
    // Use competitive_alignment_score from AI analysis pipeline
    const score = pursuit.competitive_alignment_score;
    if (score !== null && score !== undefined) {
      return { score: Math.max(0, Math.min(100, score)), source, prerequisites };
    }
    // AI analysis marked complete but no score stored — edge case
    return { score: null, source: SCORE_SOURCE.ANALYSIS_REQUIRED, prerequisites };
  }

  // No score available yet — return null with appropriate state
  return { score: null, source, prerequisites };
}

/**
 * Gets risk level based on score
 * @param {number} score - The fit score
 * @returns {string} Risk level: HIGH, MEDIUM, or LOW
 */
export function getRiskLevelFromScore(score) {
  if (score === null || score === undefined) return null;
  if (score >= 70) return 'LOW';
  if (score >= 50) return 'MEDIUM';
  return 'HIGH';
}

/**
 * Gets effective risk level - uses existing or calculates from score
 * @param {Object} pursuit - The pursuit object
 * @returns {string} Risk level
 */
export function getEffectiveRiskLevel(pursuit) {
  if (!pursuit) return null;

  // Use existing risk level if available
  if (pursuit.risk_level) {
    return pursuit.risk_level;
  }

  // Calculate from score
  const { score } = getEffectiveScore(pursuit);
  return getRiskLevelFromScore(score);
}

/**
 * Legacy: calculateMetadataBaseline — kept for backward compat but returns 0
 * Metadata no longer contributes to the Fit Score.
 */
export function calculateMetadataBaseline(pursuit, organization = null) {
  return 0;
}
